import React, { useState, useEffect, useRef } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'zh-CN', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
];

const LanguageSwitcher = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('en');
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Get saved language or detect from browser
    const saved = localStorage.getItem('preferred_language');
    if (saved) {
      setCurrentLang(saved);
    } else {
      const browserLang = (navigator.language || navigator.userLanguage).split('-')[0];
      const supported = LANGUAGES.find(l => l.code === browserLang || l.code.startsWith(browserLang));
      if (supported) {
        setCurrentLang(supported.code);
      }
    }

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode) => {
    setCurrentLang(langCode);
    localStorage.setItem('preferred_language', langCode);
    setIsOpen(false);

    // Trigger Google Translate
    const select = document.querySelector('.goog-te-combo');
    if (select) {
      select.value = langCode;
      select.dispatchEvent(new Event('change'));
    }

    // If switching to English, reset translation
    if (langCode === 'en') {
      // Try to reset by finding the restore link
      const frame = document.querySelector('.goog-te-banner-frame');
      if (frame) {
        const btn = frame.contentDocument?.querySelector('.goog-te-banner-frame-cancel');
        if (btn) btn.click();
      }
      // Alternative: reload page to clear translation
      const currentUrl = window.location.href;
      if (currentUrl.includes('#googtrans')) {
        window.location.href = currentUrl.split('#googtrans')[0];
      }
    }
  };

  const getCurrentLanguage = () => {
    return LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#0F172A] hover:text-[#0047AB] transition-colors rounded-sm border border-slate-200 hover:border-[#0047AB] bg-white"
        data-testid="language-switcher-btn"
        aria-label="Select language"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{getCurrentLanguage().name}</span>
        <span className="sm:hidden">{getCurrentLanguage().flag}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-sm shadow-lg z-50 max-h-80 overflow-y-auto"
          data-testid="language-dropdown"
        >
          <div className="py-1">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                  currentLang === lang.code ? 'bg-blue-50 text-[#0047AB]' : 'text-slate-700'
                }`}
                data-testid={`lang-option-${lang.code}`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="flex-1">{lang.name}</span>
                {currentLang === lang.code && (
                  <Check className="w-4 h-4 text-[#0047AB]" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hidden Google Translate element */}
      <div id="google_translate_element" className="hidden"></div>
    </div>
  );
};

export default LanguageSwitcher;
